﻿import { IInputScope, KbInput } from "@app/components/kb-input/kb-input.component";
import { NgComponent } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { Dirs } from "@app/helpers/dirs";
import { keyboard, Utils } from "@tools";
import * as angular from "angular";

interface IMultiAutoCompleteScope extends IInputScope {
    // external members
    source: any;
    ngModel: any[];
    /**
     * should it restrict ngModel values to ones selected in the dropdown
     */
    editable: boolean;
    /**
     * used to figure out which property from the source items should be compared to what the user is typing in
     */
    valueField: string;
    /**
     * ask the consumer to build the item given the user input.  (value: string) => ng.IPromise<any>
     */
    buildItem: any;
    ngDisabled: boolean;
    
    // internal directive members
    _matches: any[];
    _userInput: string;
    _dropdownOpen: boolean;
    _selectMatch: (item: any) => void;
    _isActive: (index: number) => boolean;
    _selectActive: (index: number) => void;
    _delete: (item: any) => void;
    _click: () => void;
    _activeIndex: number;
    _loading: boolean;
    _isEnabled: () => boolean;
    _highlightedItems: any;
    _itemMousedown: (e: ng.IAngularEvent, item: any) => void;
}

@NgComponent({
    token: Token.KbMultiAutoComplete,
    dependencies: [
        Token.$Q,
        Token.$Timeout
    ]
})
export class KbMultiAutoComplete extends KbInput {
    constructor(
        $q: ng.IQService,
        $timeout: ng.ITimeoutService
    ) {
        super($timeout);

        this.restrict = "EA";
        this.template = Dirs.component("kb-multi-auto-complete");
        this.scope = Utils.extend(this.scope, {
            source: "&",
            buildItem: "&",
            editable: "@", // should it restrict ngModel values to ones selected in the dropdown
            valueField: "@"
        });
        this.replace = true;
        this.transclude = {
            header: "?kbMultiAutoCompleteHeader",
            dropdown: "?kbMultiAutoCompleteDropdown"
        };

        this.link = (scope: IMultiAutoCompleteScope, element: JQuery, atts: any, controllers: any[]) => {
            this.kbInputLinkFn(scope, element, atts, controllers);
            let ngModelCtrl = controllers[0] as ng.INgModelController;
            if (!atts.ngModel) throw new Error("ngModel is required for kbAutoSelect");
            if (!angular.isFunction(scope.source)) {
                throw new Error("source for kbMultiAutoComplete must be a function that accepts the current query");
            }
            if (scope.buildItem && !angular.isFunction(scope.buildItem)) {
                throw new Error("buildItem for kbMultiAutoComplete must be a function that accepts the current query");
            }
            scope._userInput = null;
            scope._matches = [];
            scope._dropdownOpen = false;
            scope._loading = false;
            scope._highlightedItems = {};
            let editable = scope.$parent.$eval(atts.editable) == true;
            let $input = element.find("input");

            scope._isEnabled = () => {
                return !scope.ngDisabled;
            };

            let isFocused = () => {
                return element.hasClass("focus");
            };

            let getMatches = () => {
                let lastUserInput = scope._userInput;
                scope._loading = true;
                let promise = $q.when(scope.source({ query: scope._userInput }));
                promise.then(data => {
                    // it might happen that several async queries were in progress if a user were typing fast
                    // but we are interested only in responses that correspond to the current view value
                    if (lastUserInput === scope._userInput) {
                        scope._matches.clear();
                        scope._matches.pushArray(data);
                        scope._activeIndex = -1;
                        scope._dropdownOpen = scope._matches.length > 0;
                        scope._loading = false;
                    }
                });
            };

            scope.$watch("_userInput", (newUserInput: string) => {
                if (!newUserInput) {
                    scope._matches.clear();
                    scope._dropdownOpen = false;
                    $input.attr("size", 0);
                } else {
                    $input.attr("size", newUserInput.length);
                    getMatches();
                }
            });

            let getValue = (item: any) => {
                if (item == null) return null;
                if (scope.valueField) return item[scope.valueField];
                return item;
            };

            let buildItem = (value: any) => {
                let item = null;
                if (value) {
                    if (scope.valueField) {
                        item = {};
                        item[scope.valueField] = value;
                    } else {
                        item = value;
                    }
                    // we will add the item right away but come back to it with any modifications from the consumer
                    $q.when(scope.buildItem({ value })).then(builtItem => {
                        $.extend(item, builtItem);
                    });
                }
                return item;
            };

            scope._selectMatch = item => {
                if (scope._isEnabled() && item) {
                    let existingItem = scope.ngModel.find(i => getValue(i) == getValue(item));
                    if (!existingItem) {
                        // if (typeof item != "string") scope.ngModel.push(item);
                        // else if (atts["valueField"]) scope.ngModel.push({name: item});
                        // else scope.ngModel.push(item);
                        scope.ngModel.push(item);
                    } else {
                        scope._highlightedItems[(existingItem.name ? existingItem.name : existingItem)] = true;
                        setTimeout(() => {
                            scope.$apply((scope: IMultiAutoCompleteScope) => {
                                scope._highlightedItems[(existingItem.name ? existingItem.name : existingItem)] = false;
                            });
                        }, 500);
                    }
                    scope._userInput = "";
                }
            };

            scope._itemMousedown = (e: ng.IAngularEvent, item) => {
                e.preventDefault(); // so we don't lose focus and trigger the blur event
                scope._selectMatch(item);
            };

            scope._delete = item => {
                if (scope._isEnabled()) {
                    scope.ngModel.remove(item);
                }
            };

            scope._click = () => {
                if (scope._isEnabled()) {
                    $input.focus();
                }
            };

            scope._isActive = index => {
                return scope._activeIndex == index;
            };

            scope._selectActive = index => {
                scope._activeIndex = index;
            };

            let onDocumentClicked = (e: Event) => {
                if (editable) {
                    scope.$apply(() => {
                        scope._selectMatch(buildItem(scope._userInput));
                    });
                }
            };

            $input[0].addEventListener("focus", () => {
                element.addClass("focus");
            });
            $input[0].addEventListener("blur", () => {
                if (editable) {
                    scope.$apply(() => {
                        scope._selectMatch(buildItem(scope._userInput));
                    });
                }
                element.removeClass("focus");
            });

            // bind keyboard events: 
            // backspace(8) 
            // arrows up(38) /
            // down(40),
            // enter(13) and
            // tab(9),
            // esc(27),
            // space(32)
            // comma(188)
            $input.on("keydown", (e: JQueryEventObject) => {
                if (e.which == keyboard.backspace) {
                    if (!scope._userInput && scope.ngModel.length > 0) {
                        scope.$apply(() => {
                            scope.ngModel.pop();
                        });
                    }
                }

                if (e.which == keyboard.up) {
                    e.preventDefault();
                    scope.$apply(() => {
                        if (scope._dropdownOpen) {
                            scope._activeIndex -= 1;
                            if (scope._activeIndex < 0) {
                                scope._dropdownOpen = false;
                            }
                        }
                    });
                }
                if (e.which == keyboard.down) {
                    e.preventDefault();
                    if (scope._activeIndex < scope._matches.length - 1) {
                        scope.$apply(() => {
                            scope._activeIndex += 1;
                            scope._dropdownOpen = true;
                        });
                    }
                }
                if (e.which == keyboard.enter) {
                    scope.$apply(() => {
                        e.preventDefault();
                        if (scope._dropdownOpen) {
                            if (scope._matches.length > 0) {
                                scope._selectMatch(scope._matches[(scope._activeIndex > 0 ? scope._activeIndex : 0)]);
                            }
                        } else if (editable) {
                            scope._selectMatch(buildItem(scope._userInput));
                        } else {
                            scope._dropdownOpen = true;
                        }
                    });
                }
                if (e.which == keyboard.tab) {
                    if (scope._dropdownOpen) {
                        if (scope._matches) {
                            scope.$apply(() => {
                                scope._selectMatch(scope._matches[(scope._activeIndex > 0 ? scope._activeIndex : 0)]);
                            });
                        }
                        e.stopPropagation();
                    }
                }
                if (e.which == keyboard.escape) {
                    e.preventDefault();
                    scope.$apply(() => {
                        scope._dropdownOpen = false;
                        e.stopPropagation();
                    });
                }
                if ((e.which == keyboard.comma) && editable) {
                    e.preventDefault();
                    scope.$apply(() => {
                        scope._selectMatch(buildItem(scope._userInput));
                    });
                }
            });

            scope.$on("$destroy", () => {
                element.off();
            });
        };
    }
}
